import * as Paho from 'paho-mqtt'
import * as uuid from 'uuid'

import { Sendum } from '../../Sendum'
import { prettyPrint } from '../../utils'
import { Logger } from '../../utils/Logger'
import { getSignedUrl } from '../../utils/signV4utils'
import { WidgetsApi } from '../Api'
import { IMqttAuth } from './IMqttAuth'
import { IMqttConfiguration } from './IMqttConfiguration'
import { getMqttConfiguration } from './utils'

export class MqttClient {
    private static instance: MqttClient
    private static readonly logger: Logger = new Logger(MqttClient.name)

    private config: IMqttConfiguration | undefined
    private client: Paho.Client | undefined
    private topics: string[]
    private onMessageListeners: ((message: string, topic: string) => void)[]
    public static onConnect: () => void
    public static onMessage: (message: string) => void

    private constructor() {
        this.topics = []
        this.onMessageListeners = []
    }

    public static setConfig(config: IMqttAuth): void {
        const mqttClient = MqttClient.getInstance()
        mqttClient.config = getMqttConfiguration(config)
    }

    private static getInstance(): MqttClient {
        if (!MqttClient.instance) {
            MqttClient.instance = new MqttClient()
        }

        return MqttClient.instance
    }

    private static readonly _onConnect = (): void => {
        MqttClient.logger.debug('[_onConnect]', 'connected')
        MqttClient.subscribe(MqttClient.getInstance().topics)
        MqttClient.onConnect()
    }

    private static readonly _onConnectionLost = (e: Paho.MQTTError): void => {
        MqttClient.logger.debug('[_onConnectionLost]', e)
        WidgetsApi.getMqttAuth()
            .then(c => {
                MqttClient.setConfig(c)
                MqttClient.connect()
            })
            .catch(error => {
                MqttClient.logger.error('[_onConnectionLost]', '[AuthApi]', error)
            })
    }

    private static readonly _onMessageArrived = (message: Paho.Message): void => {
        MqttClient.logger.debug('[_onMessageArrived]', prettyPrint(message.payloadString), message.destinationName)
        MqttClient.getInstance().onMessageListeners.forEach(listener => {
            listener(message.payloadString, message.destinationName.split('/')[1])
        })
    }

    public static connect(): void {
        const config = MqttClient.getConfig()
        if (config) {
            const mqttClient = MqttClient.getInstance()
            const url = getSignedUrl(config)
            mqttClient.client = new Paho.Client(url, uuid.v4())

            mqttClient.client.onConnectionLost = MqttClient._onConnectionLost
            mqttClient.client.onMessageArrived = MqttClient._onMessageArrived

            mqttClient.client.connect({
                onSuccess: MqttClient._onConnect,
                onFailure: params => MqttClient.logger.debug('[connect] fail with', params),
                useSSL: true,
                timeout: 30,
                keepAliveInterval: 0,
                mqttVersion: 4
            })
        }
    }

    private static getConfig(): IMqttConfiguration | undefined {
        return MqttClient.getInstance().config
    }

    public static isConnected(): boolean {
        const { client } = MqttClient.getInstance()

        return client ? client.isConnected() : false
    }

    public static disconnect(): void {
        const { client } = MqttClient.getInstance()
        MqttClient.getInstance().onMessageListeners = []
        MqttClient.getInstance().topics = []
        if (client && client.isConnected()) {
            client.disconnect()
        }
    }

    public static subscribe(topics: string[]): void {
        if (!topics.length) {
            return
        }
        const config = Sendum.getConfig()
        if (config.mqttPrefix) {
            topics = topics.map(topic => (topic.startsWith(config.mqttPrefix) ? topic : config.mqttPrefix + topic))
        }
        MqttClient.logger.debug('[subscribe]', topics)

        const { client } = MqttClient.getInstance()
        if (client && client.isConnected()) {
            const perChunk = 8 // more than 8 got socket error.
            if (topics.length > perChunk) {
                const chunkedTopics = topics.reduce((resultArray: string[][], item, index) => {
                    const chunkIndex = Math.floor(index / perChunk)

                    if (!resultArray[chunkIndex]) {
                        resultArray[chunkIndex] = [] // start a new chunk
                    }

                    resultArray[chunkIndex].push(item)

                    return resultArray
                }, [])
                chunkedTopics.forEach(ts => {
                    ts.forEach(t => {
                        client.subscribe(t, {
                            onSuccess: params => MqttClient.logger.debug(`[subscribe] -> ${t} success with`, params),
                            onFailure: params => MqttClient.logger.debug(`[subscribe] -> ${t} fail with`, params)
                        })
                    })
                })
            } else {
                topics.forEach(t => {
                    client.subscribe(t, {
                        onSuccess: params => MqttClient.logger.debug(`[subscribe] -> ${t} success with`, params),
                        onFailure: params => MqttClient.logger.debug(`[subscribe] -> ${t} fail with`, params)
                    })
                })
            }
        } else {
            MqttClient.logger.debug('[subscribe]', 'client not connected')
        }

        const allTopics = [...MqttClient.getInstance().topics, ...topics]
        MqttClient.getInstance().topics = allTopics.filter((element, index) => {
            return allTopics.indexOf(element) === index
        })

        console.debug(MqttClient.getInstance().topics)
    }

    public static unsubscribe(topics: string[]): void {
        MqttClient.logger.debug('[unsubscribe]', topics)

        if (!topics.length) {
            return
        }
        const config = Sendum.getConfig()
        if (config.mqttPrefix) {
            topics = topics.map(topic => (topic.startsWith(config.mqttPrefix) ? topic : config.mqttPrefix + topic))
        }
        const { client } = MqttClient.getInstance()
        if (client && client.isConnected()) {
            topics.forEach(t => {
                client.unsubscribe(t)
            })
            MqttClient.getInstance().topics = MqttClient.getInstance().topics.filter(x => topics.indexOf(x) < 0)
        } else {
            MqttClient.logger.debug('[unsubscribe]', 'client not connected')
        }
    }

    public static addMessageListener(listener: (message: string, topic: string) => void): void {
        MqttClient.getInstance().onMessageListeners.push(listener)
    }

    public static removeMessageListener(listener: (message: string, topic: string) => void): void {
        const mqttClient = MqttClient.getInstance()
        MqttClient.logger.debug('[removeMessageListener]', mqttClient.onMessageListeners.length)
        mqttClient.onMessageListeners = mqttClient.onMessageListeners.filter(l => l !== listener)
        MqttClient.logger.debug('[removeMessageListener]', mqttClient.onMessageListeners.length)
    }

    public static unsubscribeAll(): void {
        const { client } = MqttClient.getInstance()
        if (client && client.isConnected() && MqttClient.getInstance().topics.length) {
            MqttClient.getInstance().topics.forEach(t => {
                client.subscribe(t)
            })
            MqttClient.getInstance().topics = []
        }
    }
}
